import {
  DataFromDayY,
  dataFromDayY,
  droppedStartFromDayY,
} from '../../helpers/positionCalculations'
import { MouseEvent, useEffect, useRef } from 'react'
import {
  useSchedulerContext,
  useSchedulerDispatchContext,
} from '../../context/schedulerContext'
import { dom } from '@pogokid/ui'
import { isSameDay } from '@pogokid/util/datetime'
import { useThrottleFn } from '@pogokid/react-hooks/src/useThrottleFn'
import {
  useEventPositioning,
  useMinuteHeight,
} from '../../hooks/eventHooks'
import {
  getDraggedEvent,
  setDraggedEvent,
} from '../../helpers/eventDrag'
import styles from './DayColumnAddEvent.module.css'
import { SelectedRange } from '../../types.ts'

interface WeekAddEventProps {
  today: Date
}

export function DayColumnAddEvent({ today }: WeekAddEventProps) {
  const { addEventRange, selectedEvent } = useSchedulerContext()
  const { setAddEventRange, closeDialog, onDrop } =
    useSchedulerDispatchContext()
  const { positionEvent } = useEventPositioning()
  const minuteHeight = useMinuteHeight()
  const mouseActive = useRef(false)
  const placeholderRef = useRef<HTMLDivElement | null>(null)
  const placeholderCss = useRef({ top: 0, height: 0 })
  const from = useRef<DataFromDayY | null>(null)
  const to = useRef<DataFromDayY | null>(null)
  const dropLineRef = useRef<HTMLDivElement | null>(null)

  /**
   * Where a dragged event will land, given the pointer's
   * clientY and how far below the event top it was grabbed
   */
  const droppedStart = (
    clientY: number,
    yOffset: number,
    column: HTMLElement
  ) =>
    droppedStartFromDayY({
      y: dom.offsetRelativeToParent(clientY - yOffset, column),
    })

  // The line is moved by hand rather than through state:
  // dragover fires many times a second while dragging.
  const showDropLine = (hour: number, minutes: number) => {
    const line = dropLineRef.current
    if (line) {
      line.style.top = `${
        (hour * 60 + minutes) * minuteHeight
      }px`
      line.style.display = ''
      line.textContent = `${String(hour).padStart(
        2,
        '0'
      )}:${String(minutes).padStart(2, '0')}`
    }
  }

  const hideDropLine = () => {
    if (dropLineRef.current) {
      dropLineRef.current.style.display = 'none'
    }
  }

  const updateSelectedRange = () => {
    let range: SelectedRange | undefined
    if (placeholderRef.current) {
      if (from.current && to.current) {
        const start = new Date(today)
        start.setHours(
          from.current.snapHour,
          from.current.snapMinutes
        )
        const end = new Date(today)
        end.setHours(to.current.nextHour, to.current.nextMinutes)
        range = { start, end }
        const { top, height } = positionEvent(range)
        placeholderCss.current = { top, height }
      }
      placeholderRef.current.style.top = `${placeholderCss.current.top}px`
      placeholderRef.current.style.height = `${placeholderCss.current.height}px`
    }
    return range
  }

  const onMouseMove = useThrottleFn(
    (e: MouseEvent<HTMLDivElement>) => {
      if (from.current) {
        to.current = dataFromDayY({
          y: dom.offsetRelativeToParent(
            e.clientY,
            e.target as HTMLElement
          ),
        })
      }
      updateSelectedRange()
    },
    50
  )

  useEffect(() => {
    if (addEventRange && isSameDay(today, addEventRange.start)) {
      const { top, height } = positionEvent(addEventRange)
      placeholderCss.current = { top, height }
    } else {
      placeholderCss.current = { top: 0, height: 0 }
    }
    from.current = null
    to.current = null
    updateSelectedRange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addEventRange])

  return (
    <div
      className={styles.DayColumnAddEvent}
      onMouseDown={(e) => {
        mouseActive.current = true
        // If we already have an event range or placeholder, reset and clear selection
        if (
          placeholderCss.current.height > 0 ||
          addEventRange ||
          selectedEvent
        ) {
          from.current = null
          to.current = null
          placeholderCss.current = { top: 0, height: 0 }
          closeDialog()
        } else {
          const calc = dataFromDayY({
            y: dom.offsetRelativeToParent(
              e.clientY,
              e.target as HTMLElement
            ),
          })
          from.current = calc
          to.current = calc
        }
        updateSelectedRange()
      }}
      onMouseMove={onMouseMove}
      onMouseUpCapture={() => {
        const range = updateSelectedRange()
        from.current = null
        to.current = null
        if (range) {
          setAddEventRange(range)
        }
        setTimeout(() => {
          mouseActive.current = false
        }, 10)
      }}
      onDragEnter={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      onDragOver={(e) => {
        e.currentTarget.classList.add(styles.isDragover)
        const draggedEvent = getDraggedEvent()
        if (draggedEvent) {
          const { hour, minutes } = droppedStart(
            e.clientY,
            draggedEvent.yOffset,
            e.currentTarget
          )
          showDropLine(hour, minutes)
        }
        // Preventing default behavior in the dragover event
        // "indicates that element is a draggable location",
        // as seen in MDN docs
        // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#specifying_drop_targets
        e.stopPropagation()
        e.preventDefault()
      }}
      onDragLeave={(e) => {
        e.currentTarget.classList.remove(styles.isDragover)
        hideDropLine()
      }}
      onDrop={(e) => {
        e.currentTarget.classList.remove(styles.isDragover)
        hideDropLine()
        // The dropped event moves to another column, so the
        // event that started the drag may be gone before its
        // dragend fires. Clear the drag here as well.
        setDraggedEvent(null)
        e.preventDefault()
        const { eventId, yOffset } = JSON.parse(
          e.dataTransfer.getData('text')
        )
        const { hour, minutes } = droppedStart(
          e.clientY,
          yOffset,
          e.currentTarget
        )
        const eventStart = new Date(today)
        eventStart.setHours(hour, minutes, 0, 0)
        onDrop(eventId, eventStart)
      }}
    >
      <div
        ref={placeholderRef}
        className={styles.DayColumnAddEvent__placeholder}
        style={placeholderCss.current}
      ></div>
      <div
        ref={dropLineRef}
        data-testid="day-column-drop-line"
        className={styles.DayColumnAddEvent__dropLine}
        style={{ display: 'none' }}
      ></div>
    </div>
  )
}
